<template>
  <div class="settings-page">
    <header class="page-header">
      <button type="button" class="back-btn" @click="emit('back')">
        <ArrowLeft :size="18" aria-hidden="true" />
        <span>Logs</span>
      </button>
      <h1 class="page-title">Settings</h1>
    </header>

    <!-- Section sub-nav: a tab strip so no section is buried behind the
         back-arrow. Vertical rail on desktop, horizontal scroll strip on
         mobile. Role-gated: admin-only sections only appear for admins. -->
    <nav class="settings-nav" aria-label="Settings sections">
      <button
        v-for="s in sections"
        :key="s.key"
        type="button"
        class="settings-tab"
        :class="{ active: activeSection === s.key }"
        :aria-current="activeSection === s.key ? 'page' : undefined"
        @click="setSection(s.key)"
      >
        <component :is="s.icon" :size="16" aria-hidden="true" />
        <span>{{ s.label }}</span>
      </button>
    </nav>

    <div class="settings-content">
      <!-- ================= Account ================= -->
      <section
        v-if="activeSection === 'account'"
        class="settings-section"
        aria-labelledby="settings-account-heading"
      >
        <h2 id="settings-account-heading" class="section-heading">Account</h2>

        <div class="section-body">
          <div class="field-group">
            <span class="label">Signed in as</span>
            <span class="username">{{ username || '—' }}</span>
          </div>

          <form class="pw-form" novalidate @submit.prevent="onSubmit">
            <h3 class="subsection-title">Change password</h3>

            <LgInput
              v-model="currentPw"
              type="password"
              placeholder="Current password"
              autocomplete="current-password"
              aria-label="Current password"
            />
            <LgInput
              v-model="newPw"
              type="password"
              placeholder="New password (min 8 characters)"
              autocomplete="new-password"
              aria-label="New password"
            />
            <LgInput
              v-model="confirmPw"
              type="password"
              placeholder="Confirm new password"
              autocomplete="new-password"
              aria-label="Confirm new password"
            />

            <p v-if="error" class="form-msg form-error" role="alert">{{ error }}</p>
            <p v-if="success" class="form-msg form-success" role="status">
              Password changed. Other signed-in sessions have been logged out.
              <template v-if="reloginFailed"> You may need to sign in again.</template>
            </p>

            <LgButton
              type="submit"
              variant="primary"
              size="sm"
              :disabled="submitting"
              class="submit-btn"
            >
              {{ submitting ? 'Changing…' : 'Change password' }}
            </LgButton>
          </form>

          <hr class="divider" />

          <LgButton variant="danger" size="sm" block @click="onSignOut">
            <template #icon><LogOut :size="16" aria-hidden="true" /></template>
            Sign out
          </LgButton>
        </div>
      </section>

      <!-- ================= Preferences ================= -->
      <section
        v-else-if="activeSection === 'preferences'"
        class="settings-section"
        aria-labelledby="settings-prefs-heading"
      >
        <h2 id="settings-prefs-heading" class="section-heading">Preferences</h2>

        <div class="section-body">
          <div class="field-group">
            <span class="label">Default time range</span>
            <p class="help-text">The window new Logs (and later Dashboard) sessions open with.</p>
            <LgSelect
              class="pref-select"
              :model-value="defaultTimeRange"
              :options="timeRangeOptions"
              aria-label="Default time range"
              @update:model-value="onDefaultTimeRange"
            />
          </div>

          <hr class="divider" />

          <div class="field-group">
            <span class="label">Home dashboard</span>
            <p class="help-text">
              The dashboard your Home tab lands on. Available once Dashboards ship.
            </p>
            <LgSelect
              class="pref-select"
              model-value=""
              :options="[{ value: '', label: 'No dashboards yet' }]"
              :disabled="true"
              aria-label="Home dashboard"
            />
          </div>
        </div>
      </section>

      <!-- ================= App / Org (admin only) ================= -->
      <section
        v-else-if="activeSection === 'app' && isAdmin"
        class="settings-section"
        aria-labelledby="settings-app-heading"
      >
        <h2 id="settings-app-heading" class="section-heading">App / Org settings</h2>

        <div class="section-body">
          <div class="field-group">
            <span class="label">Default pins</span>
            <p class="help-text">
              Attributes pinned by default show as columns in the log list on a fresh session (until
              someone pins their own for the view). Shared across the whole org.
            </p>
          </div>

          <p v-if="pinsError" class="form-msg form-error" role="alert">{{ pinsError }}</p>

          <div v-if="pinsLoading" class="pins-loading">Loading containers…</div>
          <p v-else-if="!containers.length" class="help-text">No containers with logs yet.</p>

          <ul v-else class="pins-list">
            <li v-for="c in containers" :key="c.container" class="pins-item">
              <span class="pins-container" :title="c.container">{{ c.container }}</span>
              <div class="pins-keys">
                <span v-for="k in defaultPins[c.container] ?? []" :key="k" class="pin-chip">
                  <span class="pin-chip-label">{{ k }}</span>
                  <button
                    type="button"
                    class="pin-chip-x"
                    :aria-label="`Remove default pin ${k} from ${c.container}`"
                    @click="removeKey(c.container, k)"
                  >
                    <X :size="12" aria-hidden="true" />
                  </button>
                </span>
                <span v-if="!(defaultPins[c.container] ?? []).length" class="pins-none">none</span>
              </div>
              <form class="pins-add" @submit.prevent="addKey(c.container)">
                <AttrKeySuggestInput
                  v-model="draftKey[c.container]"
                  class="pins-add-input"
                  :container="c.container"
                  :exclude-keys="defaultPins[c.container] ?? []"
                  placeholder="attr key"
                  :aria-label="`Add default pin key for ${c.container}`"
                />
                <LgButton type="submit" variant="ghost" size="sm">Add</LgButton>
              </form>
            </li>
          </ul>
        </div>

        <!-- Later-wave org settings: labeled seams so the IA is complete now and
             the wiring drops in when those backends land. -->
        <div class="section-body seam">
          <div class="field-group">
            <span class="label">Alert webhook</span>
            <p class="help-text">
              The org-default Discord webhook alerts notify. Set, replace, or clear it from the
              Alerts page header.
            </p>
          </div>
          <span class="seam-tag">Managed on Alerts</span>
        </div>

        <div class="section-body seam">
          <div class="field-group">
            <span class="label">Scrape targets</span>
            <p class="help-text">
              Containers and endpoints collected for Metrics. Configured here once Metrics ship.
            </p>
          </div>
          <span class="seam-tag">Coming with Metrics</span>
        </div>

        <div class="section-body seam">
          <div class="field-group">
            <span class="label">Retention</span>
            <p class="help-text">
              How long logs, metrics, and traces are kept. Currently set by deploy config.
            </p>
          </div>
          <span class="seam-tag">Env-managed for now</span>
        </div>
      </section>

      <!-- ================= Users (admin only) ================= -->
      <section
        v-else-if="activeSection === 'users' && isAdmin"
        class="settings-section"
        aria-labelledby="settings-users-heading"
      >
        <h2 id="settings-users-heading" class="section-heading">Users</h2>
        <div class="section-body">
          <UserManagement :current-user="username" />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { ArrowLeft, LogOut, X, UserRound, SlidersHorizontal, Server, Users } from 'lucide-vue-next';
import { api, ApiError, type ContainerInfo } from '../api.js';
import { useAppSettings } from '../composables/useAppSettings.js';
import {
  ATTR_KEY_RE,
  MAX_PINNED_KEYS,
  TIME_RANGES,
  RANGE_LABEL,
  DEFAULT_RANGE,
  type TimeRange,
} from '../composables/useFilters.js';
import LgInput from '../ui/LgInput.vue';
import LgButton from '../ui/LgButton.vue';
import LgSelect from '../ui/LgSelect.vue';
import UserManagement from '../components/UserManagement.vue';
import AttrKeySuggestInput from '../components/AttrKeySuggestInput.vue';

const props = defineProps<{
  username: string;
  isAdmin?: boolean;
}>();

const emit = defineEmits<{ back: []; 'sign-out': [] }>();

const isAdmin = computed(() => props.isAdmin === true);

type Section = 'account' | 'preferences' | 'app' | 'users';

const sections = computed(() => {
  const base = [
    { key: 'account' as const, label: 'Account', icon: UserRound },
    { key: 'preferences' as const, label: 'Preferences', icon: SlidersHorizontal },
  ];
  if (isAdmin.value) {
    base.push(
      { key: 'app' as const, label: 'App / Org', icon: Server },
      { key: 'users' as const, label: 'Users', icon: Users },
    );
  }
  return base;
});

const activeSection = ref<Section>('account');
const loadedSections = new Set<Section>();

function setSection(section: Section) {
  activeSection.value = section;
  ensureLoaded(section);
}

function ensureLoaded(section: Section) {
  if (loadedSections.has(section)) return;
  loadedSections.add(section);
  if (section === 'preferences') void loadPreferences();
  if (section === 'app' && isAdmin.value) void loadAppSettings();
}

// --- Preferences ----------------------------------------------------------
const timeRangeOptions = TIME_RANGES.map((r) => ({ value: r, label: RANGE_LABEL[r] }));
const defaultTimeRange = ref<TimeRange>(DEFAULT_RANGE);

async function loadPreferences() {
  try {
    const v = await api.getUserSetting<string>('defaultTimeRange');
    if (v && (TIME_RANGES as string[]).includes(v)) defaultTimeRange.value = v as TimeRange;
  } catch {
    /* leave the default; a missing/failed preference is non-fatal */
  }
}

async function onDefaultTimeRange(value: string) {
  if (!(TIME_RANGES as string[]).includes(value)) return;
  const previous = defaultTimeRange.value;
  defaultTimeRange.value = value as TimeRange; // optimistic
  try {
    await api.putUserSetting('defaultTimeRange', value);
  } catch {
    defaultTimeRange.value = previous; // roll back on failure
  }
}

// --- App / Org settings: org default pins editor --------------------------
const { defaultPins, loadDefaultPins, setContainerDefaultPins } = useAppSettings();
const containers = ref<ContainerInfo[]>([]);
const pinsLoading = ref(true);
const pinsError = ref('');
/** Per-container "add key" input drafts. */
const draftKey = reactive<Record<string, string>>({});

/** Total default pin keys across all containers — mirrors the session pin budget
 * (`MAX_PINNED_KEYS`, the ≤8-total cap `effectivePins` enforces): defaults past
 * the 8th would be silently dropped when they seed a session, so refuse them here. */
const totalDefaultPins = () => Object.values(defaultPins.value).reduce((n, ks) => n + ks.length, 0);

async function loadAppSettings() {
  pinsLoading.value = true;
  try {
    const [list] = await Promise.all([api.containers(), loadDefaultPins()]);
    containers.value = list;
  } catch (e) {
    pinsError.value = e instanceof Error ? e.message : 'Failed to load containers.';
  } finally {
    pinsLoading.value = false;
  }
}

async function persist(container: string, keys: string[]) {
  pinsError.value = '';
  try {
    await setContainerDefaultPins(container, keys);
  } catch (e) {
    pinsError.value = e instanceof Error ? e.message : 'Failed to save default pins.';
  }
}

function addKey(container: string) {
  pinsError.value = '';
  const key = (draftKey[container] ?? '').trim();
  if (!key) return;
  // Must be a real attr-shaped key (≤64 chars, ATTR_KEY_RE charset) — the server's
  // per-container route caps keys at 64, and a non-attr-shaped key can never match
  // `/api/query`'s attrs gate, so it would only ever seed a dead, valueless column.
  if (!ATTR_KEY_RE.test(key)) {
    pinsError.value =
      'Keys must be 1–64 characters using letters, numbers, dot, underscore, or dash.';
    return;
  }
  const current = defaultPins.value[container] ?? [];
  if (current.includes(key)) {
    draftKey[container] = '';
    return;
  }
  if (totalDefaultPins() >= MAX_PINNED_KEYS) {
    pinsError.value = `Default pin limit reached (${String(MAX_PINNED_KEYS)}) — remove one first.`;
    return;
  }
  draftKey[container] = '';
  persist(container, [...current, key]);
}

function removeKey(container: string, key: string) {
  const current = defaultPins.value[container] ?? [];
  persist(
    container,
    current.filter((k) => k !== key),
  );
}

// --- Change password (Account) --------------------------------------------
const currentPw = ref('');
const newPw = ref('');
const confirmPw = ref('');
const error = ref('');
const success = ref(false);
const reloginFailed = ref(false);
const submitting = ref(false);

const MIN_NEW_PASSWORD_LEN = 8;

async function onSubmit() {
  error.value = '';
  success.value = false;
  reloginFailed.value = false;

  if (!currentPw.value) {
    error.value = 'Enter your current password.';
    return;
  }
  if (newPw.value.length < MIN_NEW_PASSWORD_LEN) {
    error.value = `New password must be at least ${MIN_NEW_PASSWORD_LEN} characters.`;
    return;
  }
  if (newPw.value !== confirmPw.value) {
    error.value = 'New password and confirmation do not match.';
    return;
  }

  submitting.value = true;
  try {
    await api.changePassword(currentPw.value, newPw.value);

    // The server bumps the user's password version on change, which
    // invalidates the JWT (and thus the cookie) we're currently holding — the
    // very next authed request would 401. Transparently re-login with the new
    // password so the session continues without bouncing the user back to
    // the login screen. This is best-effort: if it fails, we still tell the
    // user their password changed successfully (it did) and note they may
    // need to sign in again.
    try {
      await api.login(props.username, newPw.value);
    } catch {
      reloginFailed.value = true;
    }

    success.value = true;
    currentPw.value = '';
    newPw.value = '';
    confirmPw.value = '';
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) {
      error.value = 'Current password is incorrect.';
    } else {
      error.value = e instanceof Error ? e.message : 'Failed to change password.';
    }
  } finally {
    submitting.value = false;
  }
}

function onSignOut() {
  emit('sign-out');
}

onMounted(() => ensureLoaded(activeSection.value));
</script>

<style scoped>
.settings-page {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
  padding: var(--lg-space-3) var(--lg-space-4);
  border-bottom: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  position: sticky;
  top: 0;
  z-index: var(--lg-z-sticky);
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-2) 0 var(--lg-space-1);
  border: 1px solid transparent;
  border-radius: var(--lg-radius-md);
  background: transparent;
  color: var(--lg-text-dim);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-md);
  cursor: pointer;
}
.back-btn:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.back-btn:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.page-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  font-weight: 600;
}

/* Section tab strip */
.settings-nav {
  display: flex;
  gap: var(--lg-space-1);
  padding: var(--lg-space-2) var(--lg-space-4);
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.settings-nav::-webkit-scrollbar {
  display: none;
}
.settings-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-shrink: 0;
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  border: 1px solid transparent;
  border-radius: var(--lg-radius-md);
  background: transparent;
  color: var(--lg-text-dim);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.settings-tab:hover:not(.active) {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.settings-tab.active {
  color: var(--lg-accent);
  background: var(--lg-bg-active);
  border-color: var(--lg-border);
}
.settings-tab:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}

.settings-content {
  max-width: 640px;
  margin: 0 auto;
  padding: var(--lg-space-4) var(--lg-space-4) var(--lg-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-6);
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
}
.section-heading {
  margin: 0;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--lg-text-faint);
}
.section-body {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
  padding: var(--lg-space-5);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-lg);
  background: var(--lg-bg-raised);
}
.section-body.seam {
  gap: var(--lg-space-3);
}
.seam-tag {
  align-self: flex-start;
  font-size: var(--lg-fs-2xs);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lg-text-faint);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-pill);
  padding: 2px var(--lg-space-2);
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.label {
  font-size: var(--lg-fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--lg-text-faint);
}
.username {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-md);
  color: var(--lg-text);
}
.pref-select {
  align-self: flex-start;
  min-width: 200px;
}
.pw-form {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.subsection-title {
  margin: 0;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  color: var(--lg-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.form-msg {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.form-error {
  color: var(--lg-danger);
}
.form-success {
  color: var(--lg-ok);
}
.submit-btn {
  align-self: flex-start;
}
.divider {
  border: none;
  border-top: 1px solid var(--lg-border-faint);
  margin: 0;
}

/* App settings: default-pins editor */
.help-text {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.pins-loading {
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.pins-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
}
.pins-item {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.pins-container {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pins-keys {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lg-space-2);
  align-items: center;
}
.pins-none {
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-faint);
}
.pin-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  padding: 2px var(--lg-space-1) 2px var(--lg-space-2);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-sm);
  background: var(--lg-bg-surface);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  color: var(--lg-text);
}
.pin-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--lg-text-faint);
  cursor: pointer;
  padding: 0;
}
.pin-chip-x:hover {
  color: var(--lg-danger);
}
.pins-add {
  display: flex;
  gap: var(--lg-space-2);
  align-items: center;
}
.pins-add-input {
  max-width: 220px;
}
</style>
